import apiClient from './client';

export const suggestGraphicType = (selections, deckId) =>
  apiClient.post('/api/concept-cards/suggest', {
    selections,
    deck_id: deckId,
  });

export const generateCard = (selections, deckId, graphicType, titleOverride, feedback) =>
  apiClient.post(
    '/api/concept-cards/generate',
    {
      selections,
      deck_id: deckId,
      graphic_type: graphicType,
      title_override: titleOverride || undefined,
      feedback: feedback || undefined,
    },
    { timeout: 120000 }
  );

export const createCard = (data) =>
  apiClient.post('/api/concept-cards/', data);

export const listCards = (deckIdOrParams) => {
  const params = {};
  if (typeof deckIdOrParams === 'string' && deckIdOrParams) {
    params.deck_id = deckIdOrParams;
  } else if (deckIdOrParams && typeof deckIdOrParams === 'object') {
    Object.assign(params, deckIdOrParams);
  }
  return apiClient.get('/api/concept-cards/', { params });
};

export const listCardsByObjective = (deckId, objectiveIdx, options = {}) =>
  apiClient.get('/api/concept-cards/by-objective', {
    params: { deck_id: deckId, objective_idx: objectiveIdx, ...options },
  });

export const getCard = (cardId) =>
  apiClient.get(`/api/concept-cards/${cardId}`);

export const updateCard = (cardId, data) =>
  apiClient.put(`/api/concept-cards/${cardId}`, data);

export const deleteCard = (cardId) =>
  apiClient.delete(`/api/concept-cards/${cardId}`);

export const bulkDeleteCards = (cardIds) =>
  apiClient.post('/api/concept-cards/bulk-delete', { card_ids: cardIds });

export const generateDrill = (cardId) =>
  apiClient.post(`/api/concept-cards/${cardId}/drill`, null, { timeout: 120000 });

export const fetchNextDrillQuestion = (cardId, { abTest = true } = {}) =>
  apiClient
    .get('/api/concept-cards/drill/next', {
      params: {
        scope: `card:${cardId}`,
        ...(abTest ? { ab_test: true } : {}),
      },
    })
    .then((r) => r.data);

export const fetchDrillProgress = (cardId) =>
  apiClient
    .get('/api/concept-cards/drill/progress', {
      params: { scope: `card:${cardId}` },
    })
    .then((r) => r.data);

export const submitDrillAnswer = (cardId, bankItemId, correct) =>
  apiClient.post('/api/concept-cards/drill/answer', {
    scope: `card:${cardId}`,
    bank_item_id: bankItemId,
    correct,
  });

export const submitDrillResult = (cardId, questionId, correct) =>
  apiClient.post(`/api/concept-cards/${cardId}/drill-result`, {
    question_id: questionId,
    correct,
  });

export const rateDrillQuestion = (bankItemId, rating, notes) =>
  apiClient.post(`/api/concept-cards/drill/${bankItemId}/rate`, {
    rating,
    notes: notes || undefined,
  });

// Met "Heal" action — pick a random concept card scoped to the combat's
// exam+area, then submit drill outcome to apply the Met-scaled heal +1 lactic.
// Server route: backend/api/combat_heal_action.py
export const healActionPickCard = (sessionId, { dlc, areaKey, abTest = false } = {}) =>
  apiClient
    .post(`/api/combat/heal-action/${sessionId}/pick-card`, {
      dlc: dlc || null,
      area_key: areaKey || null,
      ab_test: !!abTest,
    })
    .then((r) => r.data);

export const healActionSubmit = (sessionId, { cardId, bankItemId, correct, clientActionId }) =>
  apiClient
    .post(`/api/combat/heal-action/${sessionId}/submit`, {
      card_id: cardId,
      bank_item_id: bankItemId,
      correct: !!correct,
      client_action_id: clientActionId || null,
    })
    .then((r) => r.data);

export const shareCard = (cardId, anonymous = false) =>
  apiClient.post(`/api/concept-cards/${cardId}/share`, { anonymous });

export const unshareCard = (cardId) =>
  apiClient.post(`/api/concept-cards/${cardId}/unshare`);

// Community browse & shared drill
export const browseCommunityCards = (params = {}) =>
  apiClient.get('/api/concept-cards/browse', { params });

export const getSharedCard = (cardId) =>
  apiClient.get(`/api/concept-cards/shared/${cardId}`);

export const generateSharedDrill = (cardId) =>
  apiClient.post(`/api/concept-cards/shared/${cardId}/drill`, null, { timeout: 120000 });

export const fetchNextSharedDrillQuestion = (cardId, { abTest = true } = {}) =>
  apiClient
    .get('/api/concept-cards/drill/next', {
      params: {
        scope: `shared:${cardId}`,
        ...(abTest ? { ab_test: true } : {}),
      },
    })
    .then((r) => r.data);

export const fetchSharedDrillProgress = (cardId) =>
  apiClient
    .get('/api/concept-cards/drill/progress', {
      params: { scope: `shared:${cardId}` },
    })
    .then((r) => r.data);

export const submitSharedDrillAnswer = (cardId, bankItemId, correct) =>
  apiClient.post('/api/concept-cards/drill/answer', {
    scope: `shared:${cardId}`,
    bank_item_id: bankItemId,
    correct,
  });

export const submitSharedDrillResult = (cardId, questionId, correct) =>
  apiClient.post(`/api/concept-cards/shared/${cardId}/drill-result`, {
    question_id: questionId,
    correct,
  });

// Generate cards from cached synthesis for a specific objective
export const generateFromSynthesis = (deckId, objectiveIdx) =>
  apiClient.post(`/api/concept-cards/from-synthesis?deck_id=${deckId}&objective_idx=${objectiveIdx}`, null, {
    timeout: 120000,
  });

// DEV TEST: toggle a review flag on a concept card (admin-only).
export const setCardReviewFlag = (cardId, flagged, note) =>
  apiClient.post(`/api/concept-cards/${cardId}/review-flag`, {
    flagged: !!flagged,
    note: note || null,
  });

// DEV TEST: list all cards currently flagged for review (admin-only).
export const listReviewFlaggedCards = (limit) =>
  apiClient.get(`/api/concept-cards/review-flagged${limit ? `?limit=${limit}` : ''}`);

// Auto-suggest (prebuilt cards)
export const autoSuggest = (deckId, force = false) =>
  apiClient.post(`/api/concept-cards/auto-suggest/${deckId}?force=${force}`, null, {
    timeout: 120000,
  });

export const autoConfirm = (suggestionId, titleOverride, graphicTypeOverride) =>
  apiClient.post('/api/concept-cards/auto-confirm', {
    suggestion_id: suggestionId,
    title_override: titleOverride || undefined,
    graphic_type_override: graphicTypeOverride || undefined,
  }, { timeout: 60000 });

export const autoDismiss = (suggestionId) =>
  apiClient.post('/api/concept-cards/auto-dismiss', {
    suggestion_id: suggestionId,
  });

export const cloneSharedCard = (cardId) =>
  apiClient.post(`/api/concept-cards/clone/${cardId}`);

// Recommendation
export const getRecommendation = (deckIds) =>
  apiClient.get('/api/concept-cards/recommendation', {
    params: deckIds?.length ? { deck_ids: deckIds.join(',') } : {},
  });

// Topic drill stats
export const getTopicDrillStats = (deckIds) =>
  apiClient.get('/api/concept-cards/topic-drill-stats', {
    params: { deck_ids: deckIds.join(',') },
  });

// Slide coverage — which slides in a deck have concept cards
export const getSlideCoverage = (deckId) =>
  apiClient.get('/api/concept-cards/slide-coverage', {
    params: { deck_id: deckId },
  });

export const getForgeableSignal = (params, config = {}) =>
  apiClient.get('/api/concept-cards/forgeable-signal', {
    params,
    ...config,
  });
